import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ViewStyle } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import dayjs from 'dayjs';
import type { CalendarEvent } from './WeekTimelineView';

// Status (stage) badge colors – matches palette used elsewhere in app
// (see src/screens/dashboard/InfoWindowCard.tsx stageBadgeColors)
export const getStageBadgeColors = (stage?: string): { bg: string; fg: string } => {
  const s = (stage || '').toLowerCase().trim();
  switch (s) {
    case 'draft':       return { bg: '#E5E7EB', fg: '#374151' };
    case 'provisional': return { bg: '#262E32', fg: '#FFFFFF' };
    case 'confirmed':   return { bg: '#4379FF', fg: '#FFFFFF' };
    case 'checked out': return { bg: '#B8393C', fg: '#FFFFFF' };
    case 'checked in':  return { bg: '#4CBA70', fg: '#FFFFFF' };
    case 'canceled':
    case 'cancelled':   return { bg: '#666666', fg: '#FFFFFF' };
    default:            return { bg: '#E5E7EB', fg: '#374151' };
  }
};

export interface EventCardProps {
  event: CalendarEvent;
  onPress?: () => void;
  /** 'compact' tightens padding/fonts (used in month chips & short timeline blocks). */
  density?: 'compact' | 'full';
  /** For multi-day spanning bars in month view */
  continuesBefore?: boolean;
  continuesAfter?: boolean;
  /** Field visibility (timeline view toggles based on available height) */
  showTime?: boolean;
  showDateRange?: boolean;
  showCustomer?: boolean;
  showBrand?: boolean;
  showStatus?: boolean;
  /** Wrapper style override (height, position, etc.) */
  style?: ViewStyle;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onPress,
  density = 'full',
  continuesBefore = false,
  continuesAfter = false,
  showTime = true,
  showDateRange = true,
  showCustomer = true,
  showBrand = true,
  showStatus = true,
  style,
}) => {
  const accent = event.color || '#9CA3AF';
  // Derive readable text colors based on the plantation background
  const isLight = (() => {
    const c = accent.replace('#', '');
    if (c.length !== 6) return true;
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 150;
  })();
  const fgPrimary = isLight ? '#111111' : '#FFFFFF';
  const fgSecondary = isLight ? '#333333' : 'rgba(255,255,255,0.92)';
  const fgMuted = isLight ? '#555555' : 'rgba(255,255,255,0.78)';
  const metaDotColor = isLight ? '#999999' : 'rgba(255,255,255,0.55)';
  const borderColor = isLight ? 'rgba(0,0,0,0.13)' : 'rgba(255,255,255,0.25)';
  const dots = event.categoryColors || [];
  const isAllDay = !event.startTime && !event.endTime;
  const startD = dayjs(event.start);
  const endD = dayjs(event.end);
  const earlier = startD.isAfter(endD) ? endD : startD;
  const later = startD.isAfter(endD) ? startD : endD;
  const sameDay = earlier.isSame(later, 'day');
  const dayCount = later.startOf('day').diff(earlier.startOf('day'), 'day') + 1;
  const dur = Math.abs(later.diff(earlier, 'minute'));
  const h = Math.floor(dur / 60);
  const m = dur % 60;
  const durationText = isAllDay
    ? 'All day'
    : h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  const stageColors = getStageBadgeColors(event.stage);

  const compact = density === 'compact';
  const fontTitle = compact ? 13 : 16;
  const fontMeta = compact ? 11 : 13;
  const padV = compact ? 6 : 12;
  const padH = compact ? 8 : 14;

  const timeLabel = isAllDay
    ? null
    : event.startTime && event.endTime
      ? `${earlier.format('h:mm A')} – ${later.format('h:mm A')}`
      : event.startTime
        ? earlier.format('h:mm A')
        : null;

  const radii = {
    borderTopLeftRadius: continuesBefore ? 0 : 8,
    borderBottomLeftRadius: continuesBefore ? 0 : 8,
    borderTopRightRadius: continuesAfter ? 0 : 8,
    borderBottomRightRadius: continuesAfter ? 0 : 8,
  };

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={onPress}
      style={[styles.card, radii, { backgroundColor: accent, borderColor }, style]}
    >
      <View style={[styles.body, { paddingHorizontal: padH, paddingVertical: padV }]}>
        <View style={styles.titleRow}>
          <FontAwesome5
            name="hashtag"
            size={compact ? 11 : 13}
            color={fgPrimary}
            solid
            style={{ marginRight: 6 }}
          />
          <Text
            style={[styles.title, { fontSize: fontTitle, color: fgPrimary }]}
            numberOfLines={1}
          >
            {event.title}
          </Text>
        </View>

        {showTime && timeLabel && (
          <View style={styles.metaRow}>
            <FontAwesome5
              name="clock"
              size={compact ? 10 : 11}
              color={fgSecondary}
              solid
              style={{ marginRight: 6 }}
            />
            <Text
              style={[styles.metaText, { fontSize: fontMeta, color: fgSecondary }]}
              numberOfLines={1}
            >
              {timeLabel}
            </Text>
            {!compact && (
              <>
                <View style={[styles.metaDot, { backgroundColor: metaDotColor }]} />
                <Text
                  style={[styles.metaSubtle, { fontSize: fontMeta, color: fgMuted }]}
                  numberOfLines={1}
                >
                  {durationText}
                </Text>
              </>
            )}
          </View>
        )}

        {showDateRange && !sameDay && (
          <View style={styles.metaRow}>
            <FontAwesome5
              name="calendar-alt"
              size={compact ? 10 : 11}
              color={fgSecondary}
              solid
              style={{ marginRight: 6 }}
            />
            <View style={styles.multiDayBadge}>
              <Text style={styles.multiDayBadgeText} numberOfLines={1}>
                {earlier.format('MMM D')} – {later.format('MMM D')} · {dayCount}d
              </Text>
            </View>
          </View>
        )}

        {showCustomer && event.customer && (
          <View style={styles.metaRow}>
            <FontAwesome5
              name="user"
              size={compact ? 10 : 12}
              color={fgPrimary}
              solid
              style={{ marginRight: 6 }}
            />
            <Text
              style={[styles.customer, { fontSize: fontMeta, color: fgPrimary }]}
              numberOfLines={1}
            >
              {event.customer}
            </Text>
          </View>
        )}

        {showBrand && event.brand && (
          <View style={styles.metaRow}>
            <FontAwesome5
              name="tag"
              size={compact ? 10 : 11}
              color={fgMuted}
              solid
              style={{ marginRight: 6 }}
            />
            <Text
              style={[styles.brand, { fontSize: Math.max(fontMeta - 1, 10), color: fgMuted }]}
              numberOfLines={1}
            >
              {event.brand}
            </Text>
          </View>
        )}

        {showStatus && event.stage && (
          <View style={[styles.statusChip, { backgroundColor: stageColors.bg }]}>
            <Text style={[styles.statusChipText, { color: stageColors.fg }]} numberOfLines={1}>
              {event.stage}
            </Text>
          </View>
        )}
      </View>

      {dots.length > 0 && (
        <View style={styles.dotsTopRight}>
          {dots.map((c, i) => (
            <View
              key={`${c}-${i}`}
              style={[
                styles.categoryDot,
                compact && styles.categoryDotSm,
                { backgroundColor: c },
              ]}
            />
          ))}
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.16,
    shadowRadius: 3,
    elevation: 3,
  },
  body: { flex: 1 },
  titleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 2 },
  title: { fontWeight: '700', flex: 1 },
  metaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4 },
  metaText: { fontWeight: '500' },
  metaSubtle: {},
  metaDot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    marginHorizontal: 6,
  },
  customer: { fontWeight: '600' },
  brand: { fontWeight: '500' },
  multiDayBadge: {
    backgroundColor: '#FEF3C7',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  multiDayBadgeText: { fontSize: 11, color: '#92400E', fontWeight: '600' },
  statusChip: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 6,
  },
  statusChipText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
  dotsTopRight: {
    position: 'absolute',
    top: 6,
    right: 6,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  categoryDot: { width: 8, height: 8, borderRadius: 4, marginLeft: 3 },
  categoryDotSm: { width: 6, height: 6, borderRadius: 3 },
});

export default EventCard;
